import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BookOpen, Target, Award, TrendingUp, GraduationCap, Package,
  FileText, Globe, ArrowRight, Mail, Linkedin, Twitter, Briefcase,
  CheckCircle2, Calendar,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { useCurrency, appendCurrency } from '../context/CurrencyContext';
import { ZHELTA_SHOP_URL } from '../mock';

/* Smooth-scroll a hash like `#our-story` to its section, accounting for the
   sticky header. Runs on mount and whenever the URL hash changes. */
function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const id = hash.replace('#', '');
    const target = document.getElementById(id);
    if (target) {
      const offset = 140; // sticky header (utility bar + main bar)
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [hash]);
}

function Section({ id, eyebrow, title, children, dark = false, accent = '#18181b' }) {
  return (
    <section
      id={id}
      data-testid={`about-section-${id}`}
      className={`scroll-mt-[140px] py-20 sm:py-24 ${dark ? 'bg-[#f5f5f7] text-zinc-900 border-y border-zinc-200' : 'bg-white text-gray-900'}`}
    >
      <div className="zh-container">
        {eyebrow && (
          <div
            className={`text-[11px] font-bold tracking-[0.22em] uppercase mb-3 ${dark ? 'text-zinc-500' : ''}`}
            style={!dark ? { color: accent } : undefined}
          >
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-8 max-w-3xl">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

function StatCard({ value, label }) {
  return (
    <div className="rounded-2xl border border-zinc-300/60 bg-white p-6">
      <div className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
}

const LEADERSHIP = [
  {
    name: 'Bernard A. Tettehfio',
    role: 'Founder & CEO',
    bio: 'Founded ZHELTA to bridge the technological gap for everyday users and small businesses. Sets the company vision and leads the systems practice end-to-end.',
    initials: 'BT',
    photo: '/team/bernard.jpg',
  },
  {
    name: 'Daniel MacCarthy',
    role: 'Marketing Director',
    bio: 'Builds the brand, owns go-to-market across all three divisions, and partners with vendors and educators to bring ZHELTA closer to the communities it serves.',
    initials: 'DM',
    photo: '/team/daniel.jpg',
  },
];

const VALUES = [
  {
    icon: Target,
    title: 'Solution-oriented',
    body: 'We identify problems and resolve them. Every engagement starts with diagnosis, ends with a working outcome — not a long list of caveats.',
  },
  {
    icon: CheckCircle2,
    title: 'Technology for everyone',
    body: 'We exist to bridge the technological gap so every individual can use technology to the best of their ability — at school, at work, or at home.',
  },
  {
    icon: Award,
    title: 'Earn the trust',
    body: 'No long-term lock-in contracts. Transparent pricing. Honest communication. We keep clients because we deserve to.',
  },
  {
    icon: TrendingUp,
    title: 'Compound learning',
    body: 'Systems, the marketplace, and the academy feed each other. Clients become learners, learners become engineers, engineers become better partners.',
  },
];

const PRESS = [
  {
    date: 'July 3, 2024',
    title: 'ZHELTA officially launches full operations as a unified three-division technology company',
    source: 'Company announcement',
  },
  {
    date: '2024',
    title: 'ZHELTA Systems opens its IT troubleshooting and managed services practice for individuals and small businesses',
    source: 'Company announcement',
  },
  {
    date: '2024',
    title: 'ZHELTA Academy opens enrolment for practical, expert-led IT education programs',
    source: 'Company announcement',
  },
];

const CAREERS = [
  { title: 'Senior Site Reliability Engineer', team: 'Systems', loc: 'Remote · Global' },
  { title: 'Cybersecurity Analyst — L2', team: 'Systems', loc: 'Remote · Global' },
  { title: 'Curriculum Designer (Cloud Track)', team: 'Academy', loc: 'Remote · Global' },
  { title: 'Vendor Partnerships Manager', team: 'Marketplace', loc: 'Remote · Global' },
  { title: 'Client Support & Operations Specialist', team: 'Support', loc: 'Remote · Global' },
];

export default function About() {
  useHashScroll();
  const { currency } = useCurrency();

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        pageKey="about"
        path="/about"
        title="About ZHELTA — A Technology Solution-Oriented Company"
        description="ZHELTA has been supporting individuals and small businesses since 2012, and launched full operations on July 3, 2024. Three integrated divisions — Systems, Services, and Academy — bridging the technological gap for everyone."
      />
      <Header />

      <main>
        {/* Hero */}
        <section
          className="relative bg-white text-zinc-900 overflow-hidden border-b border-zinc-200"
        >
          <div className="relative zh-container py-24 sm:py-32">
            <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-4 text-zinc-500">About ZHELTA</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl mb-6 text-zinc-900">
              A technology solution-oriented company.
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 max-w-3xl mb-10 leading-relaxed">
              ZHELTA exists to bridge the technological gap — empowering every individual and small
              business to use technology to the best of their ability. We identify problems and
              resolve them, across three integrated divisions: Systems, Services, and Academy.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
              <StatCard value="2012" label="Serving SMBs since" />
              <StatCard value="Jul 2024" label="Full operations launch" />
              <StatCard value="3" label="Integrated divisions" />
              <StatCard value="14" label="Supported languages" />
            </div>
          </div>
        </section>

        {/* Our Story */}
        <Section id="our-story" eyebrow="Our Story" title="Twelve years of helping small businesses and individuals get technology right.">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-5 text-[15px] leading-7 text-gray-700">
              <p>
                ZHELTA began in <strong>2012</strong> as a small technology support practice for individuals and small-scale
                business owners — the kind of clients who could not justify enterprise IT contracts but still needed
                their systems to work, their data to be safe, and their tools to actually help them grow.
              </p>
              <p>
                For more than a decade we operated quietly, one client at a time, learning the practical realities of
                running technology in the everyday world: tight budgets, mixed hardware, multilingual teams, and zero
                tolerance for downtime. That hands-on experience shaped every product we ship today.
              </p>
              <p>
                On <strong>July 3, 2024</strong>, ZHELTA launched into full operations as a unified technology company organised
                around three integrated divisions:
              </p>
              <ul className="space-y-2.5 pl-1">
                <li className="flex gap-3">
                  <span className="inline-flex w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 items-center justify-center text-[12px] font-extrabold shrink-0">1</span>
                  <span><strong>ZHELTA Systems</strong> — IT-related troubleshooting, managed services, cybersecurity, and infrastructure support for individuals and businesses.</span>
                </li>
                <li className="flex gap-3">
                  <span className="inline-flex w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 items-center justify-center text-[12px] font-extrabold shrink-0">2</span>
                  <span><strong>ZHELTA Services</strong> — our e-store, where verified hardware, software, and accessories are matched to real-world needs and localised pricing.</span>
                </li>
                <li className="flex gap-3">
                  <span className="inline-flex w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 items-center justify-center text-[12px] font-extrabold shrink-0">3</span>
                  <span><strong>ZHELTA Academy</strong> — practical, expert-led education that prepares learners for in-demand technology careers.</span>
                </li>
              </ul>
              <p>
                Each division reinforces the others: clients become learners, learners become engineers, engineers
                continue to raise the bar for the clients we support next.
              </p>
            </div>
            <aside className="rounded-2xl bg-white border border-zinc-300 p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-700 mb-2">Milestones</div>
              <ul className="space-y-4 text-[13px] text-gray-700">
                <li>
                  <div className="text-zinc-700 font-extrabold text-base">2012</div>
                  <div>Started supporting individuals and small-scale business owners.</div>
                </li>
                <li>
                  <div className="text-zinc-700 font-extrabold text-base">July 3, 2024</div>
                  <div>Launched full operations as a unified three-division platform.</div>
                </li>
                <li>
                  <div className="text-zinc-700 font-extrabold text-base">Today</div>
                  <div>Systems · Services · Academy — under one roof, one mission.</div>
                </li>
              </ul>
            </aside>
          </div>
        </Section>

        {/* Mission & Values */}
        <Section id="mission" eyebrow="Mission &amp; Values" title="Bridge the technological gap." dark>
          <p className="text-lg text-zinc-700 max-w-3xl mb-10 leading-relaxed">
            Our mission is to bridge the technological gap by allowing every individual to utilise technology
            to the best of their ability. We are a technology solution-oriented company: we identify problems
            and resolve them — for the home user, the small business, and the next generation of learners.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-zinc-300 transition-colors">
                <v.icon className="w-6 h-6 text-[#c9a227] mb-3" strokeWidth={2} />
                <h3 className="text-lg font-bold mb-2 text-zinc-900">{v.title}</h3>
                <p className="text-sm text-zinc-600 leading-6">{v.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Leadership */}
        <Section id="leadership" eyebrow="Leadership" title="The team behind ZHELTA.">
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl">
            {LEADERSHIP.map((p) => (
              <div key={p.name} data-testid={`about-leader-${p.initials.toLowerCase()}`} className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                {p.photo ? (
                  <div className="w-20 h-20 rounded-full overflow-hidden mb-4 ring-2 ring-zinc-300 bg-white shadow-sm">
                    <img
                      src={p.photo}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center top' }}
                    />
                  </div>
                ) : (
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-white font-extrabold text-2xl mb-4 bg-zinc-900"
                  >
                    {p.initials}
                  </div>
                )}
                <div className="text-base font-bold text-gray-900 mb-0.5">{p.name}</div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-zinc-700 mb-3">{p.role}</div>
                <p className="text-sm text-gray-600 leading-6">{p.bio}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[14px] text-gray-500 max-w-2xl">
            Plus a growing team of proficient engineers, instructors, and support specialists across Systems,
            Services, and Academy — every one of them committed to keeping our promise of practical, accessible technology.
          </p>
        </Section>

        {/* Careers */}
        <Section id="careers" eyebrow="Join Us · Careers" title="We hire builders who like shipping.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            We are fully remote and global. Hire wherever the talent is — work wherever you do your best work. Below are the roles open right now.
            Don’t see your role? Email us anyway — talent finds a way in.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {CAREERS.map((c) => (
              <div key={c.title} data-testid={`about-career-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="rounded-2xl border border-gray-200 bg-white p-5 flex items-start justify-between gap-4 hover:border-zinc-400 transition-colors">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-700 mb-1">{c.team}</div>
                  <div className="text-base font-bold text-gray-900 mb-1">{c.title}</div>
                  <div className="text-[13px] text-gray-500">{c.loc}</div>
                </div>
                <a
                  href="mailto:careers@zhelta.com?subject=Application: {c.title}"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold text-white"
                  style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
                >
                  Apply <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
          <a
            href="mailto:careers@zhelta.com"
            data-testid="about-careers-email"
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-zinc-700 hover:underline"
          >
            <Mail className="w-4 h-4" /> careers@zhelta.com
          </a>
        </Section>

        {/* Internships */}
        <Section id="internships" eyebrow="Join Us · Internships" title="Six months. Real on-call. Real shipping.">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl bg-white border border-gray-200 p-7">
              <p className="text-[15px] text-gray-700 leading-7 mb-5">
                The ZHELTA Internship is a six-month, paid program for Academy graduates and university students. Interns are
                embedded with a full-time engineer, ship to production from week three, and rotate across Systems, Marketplace,
                and Academy depending on their track.
              </p>
              <ul className="space-y-2 text-[14px] text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Paid + remote-friendly + visa-portable</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Two cohorts per year (Spring &amp; Fall)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> 70%+ conversion to full-time offers</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Mentor pair, structured curriculum, real on-call rotation</li>
              </ul>
            </div>
            <div
              className="rounded-2xl bg-white border border-zinc-200 text-zinc-900 p-7"
            >
              <Calendar className="w-6 h-6 text-zinc-700 mb-3" />
              <div className="text-sm uppercase tracking-[0.18em] text-zinc-500 mb-1">Next cohort</div>
              <div className="text-2xl font-extrabold mb-4">Sept 1, 2026</div>
              <p className="text-sm text-zinc-600 mb-5">Applications open July 1. Rolling interviews until 30 spots are filled.</p>
              <a
                href="mailto:careers@zhelta.com?subject=Internship Application"
                data-testid="about-internships-apply"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-zinc-800 bg-white text-[13px] font-semibold hover:brightness-95 transition-all"
              >
                Apply Now <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </Section>

        {/* Become a Vendor */}
        <Section id="become-a-vendor" eyebrow="Join Us · Marketplace" title="List your hardware on the ZHELTA Marketplace.">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-4 text-[15px] text-gray-700 leading-7">
              <p>
                The ZHELTA Marketplace serves individuals, small businesses, and IT teams with verified hardware in
                localised currencies. If you sell verified networking, security, storage, or computing hardware, we want you on the catalog.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><Package className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Verified-vendor badge on every listing</li>
                <li className="flex items-start gap-2"><Globe className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Multi-currency, multi-language pricing</li>
                <li className="flex items-start gap-2"><Briefcase className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Direct B2B invoicing &amp; bulk-order pipeline</li>
                <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Co-marketing across our channels</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-zinc-300 bg-white p-7">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-700 mb-2">VENDOR PROGRAM</div>
              <h3 className="text-xl font-extrabold mb-3">Apply in under 5 minutes.</h3>
              <p className="text-sm text-gray-700 mb-5">Send us your catalog (PDF or CSV) and our partnerships team will reply within 2 business days.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={appendCurrency(ZHELTA_SHOP_URL, currency)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-vendor-shop"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[13px] font-semibold"
                  style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
                >
                  Visit Marketplace <ArrowRight className="w-3.5 h-3.5" />
                </a>
                <a
                  href="mailto:sales@zhelta.com?subject=Vendor Application"
                  data-testid="about-vendor-email"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> sales@zhelta.com
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* Press / Company milestones */}
        <Section id="press" eyebrow="Newsroom · Press" title="Company milestones.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            A short, honest log of the announcements we&rsquo;ve made. We&rsquo;ll keep adding to this list as ZHELTA grows.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRESS.map((p, i) => (
              <article key={p.title} data-testid={`about-press-${i + 1}`} className="rounded-2xl border border-gray-200 bg-white p-6 hover:border-zinc-400 transition-colors">
                <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-700 mb-2">{p.date}</div>
                <h3 className="text-base font-bold text-gray-900 mb-3 leading-snug">{p.title}</h3>
                <a href="mailto:info@zhelta.com?subject=Request%20for%20more%20information" className="text-[13px] font-semibold text-zinc-700 hover:underline inline-flex items-center gap-1">
                  Request details <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </article>
            ))}
          </div>
          <div className="mt-8 text-sm text-gray-600">
            Press inquiries: <a href="mailto:info@zhelta.com" className="text-zinc-700 font-semibold hover:underline">info@zhelta.com</a>
          </div>
        </Section>

        {/* Why ZHELTA — confident commitments grounded in 12+ years of practice */}
        <Section id="news" eyebrow="Why ZHELTA" title="Built on more than a decade of practice." dark>
          <p className="text-lg text-zinc-700 max-w-3xl mb-10 leading-relaxed">
            ZHELTA isn&rsquo;t a startup learning the basics. We have been supporting individuals and
            small-scale business owners since <strong className="text-zinc-900">2012</strong> — long before we became
            a unified three-division company. Every commitment below is grounded in twelve years of doing
            the actual work.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">A decade in practice</div>
              <h3 className="text-lg font-bold mb-2 text-zinc-900">Experience, not experimentation.</h3>
              <p className="text-sm text-zinc-600 leading-6">
                Twelve+ years working hands-on with individuals and small businesses — long before managed
                IT was a buzzword. We&rsquo;ve already made the mistakes other providers are still learning to avoid.
              </p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">One team, three divisions</div>
              <h3 className="text-lg font-bold mb-2 text-zinc-900">Coordinated, never siloed.</h3>
              <p className="text-sm text-zinc-600 leading-6">
                Systems for IT support. Services for verified hardware. Academy for skills. All under one roof
                so nothing falls through the cracks between vendor, technician, and trainer.
              </p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Solution-oriented end to end</div>
              <h3 className="text-lg font-bold mb-2 text-zinc-900">Diagnose. Resolve. Ship.</h3>
              <p className="text-sm text-zinc-600 leading-6">
                We identify problems and resolve them. No padded invoices, no long lists of caveats, no
                disappearing after handoff. The job is done when it&rsquo;s working.
              </p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Built for your reality</div>
              <h3 className="text-lg font-bold mb-2 text-zinc-900">Accessible by design.</h3>
              <p className="text-sm text-zinc-600 leading-6">
                Multi-language, multi-currency, and transparent pricing from day one. Your business should
                never need to translate itself to work with its technology partner.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href="/systems/contact"
              data-testid="about-why-talk-to-us"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#f5a623] text-[#0e0e10] text-[13px] font-semibold hover:brightness-105 transition-all"
            >
              Talk to ZHELTA <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <a
              href="/systems/solutions"
              data-testid="about-why-solutions"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-zinc-300 text-zinc-900 text-[13px] font-semibold hover:bg-[#f5a623] hover:text-[#0e0e10] hover:border-[#f5a623] transition-colors"
            >
              See what we do
            </a>
          </div>
        </Section>

        {/* CTA */}
        <section className="bg-white py-20">
          <div className="zh-container text-center">
            <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Want the full story?</h2>
            <p className="text-base text-gray-700 max-w-xl mx-auto mb-8">
              Send us a note — we love hearing from press, partners, candidates, and curious humans alike.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="mailto:info@zhelta.com"
                data-testid="about-final-cta-email"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[14px] font-semibold"
                style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
              >
                <Mail className="w-4 h-4" /> info@zhelta.com
              </a>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-semibold text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-colors"
              >
                Contact Form <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
